// AGC: Client-side image quality gate for product detection pipeline
// AGC: Runs before uploading to backend to prevent wasted API calls
// AGC: Uses Canvas API only -- no paid APIs

import type { ImageQualityResult } from '../types/inventory'

// =============================================================================
// AGC: Configuration thresholds
// =============================================================================

const THRESHOLDS = {
  /** AGC: Laplacian variance below this = blurry (lowered to avoid false positives) */
  BLUR_VARIANCE_MIN: 15,
  /** AGC: Average luminance below this = too dark */
  BRIGHTNESS_MIN: 25,
  /** AGC: Average luminance above this = too bright / washed out */
  BRIGHTNESS_MAX: 240,
  /** AGC: Minimum width in pixels */
  RESOLUTION_MIN_WIDTH: 320,
  /** AGC: Minimum height in pixels */
  RESOLUTION_MIN_HEIGHT: 240,
  /** AGC: Edge density below this = likely no content visible (very lenient) */
  EDGE_DENSITY_MIN: 0.005,
}

// =============================================================================
// AGC: Main quality check function
// =============================================================================

/**
 * AGC: Analyzes an image blob for quality issues before uploading.
 * AGC: Returns pass/fail with user-friendly hints for retake.
 * @param imageBlob - The captured image as a Blob
 * @returns ImageQualityResult with pass status, warnings, and hints
 */
export async function checkImageQuality(imageBlob: Blob): Promise<ImageQualityResult> {
  const warnings: string[] = []
  const hints: string[] = []

  try {
    // AGC: Load image into a canvas for pixel analysis
    const imageBitmap = await createImageBitmap(imageBlob)
    const { width, height } = imageBitmap

    // AGC: Use a smaller canvas for performance (max 800px on longest edge)
    const scale = Math.min(1, 800 / Math.max(width, height))
    const cw = Math.round(width * scale)
    const ch = Math.round(height * scale)

    const canvas = document.createElement('canvas')
    canvas.width = cw
    canvas.height = ch
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      // AGC: Canvas not available -- skip quality check, allow upload
      return { pass: true, warnings: [], hints: [] }
    }

    ctx.drawImage(imageBitmap, 0, 0, cw, ch)
    const imageData = ctx.getImageData(0, 0, cw, ch)
    const pixels = imageData.data

    // AGC: 1. Resolution check (on original dimensions)
    if (width < THRESHOLDS.RESOLUTION_MIN_WIDTH || height < THRESHOLDS.RESOLUTION_MIN_HEIGHT) {
      warnings.push('low_resolution')
      hints.push('Move closer to the product label')
    }

    // AGC: 2. Convert to grayscale and compute brightness
    const gray = toGrayscale(pixels, cw, ch)
    const avgBrightness = gray.reduce((s, v) => s + v, 0) / gray.length

    if (avgBrightness < THRESHOLDS.BRIGHTNESS_MIN) {
      warnings.push('too_dark')
      hints.push('Improve lighting or use flash')
    } else if (avgBrightness > THRESHOLDS.BRIGHTNESS_MAX) {
      warnings.push('too_bright')
      hints.push('Reduce glare or move away from direct light')
    }

    // AGC: 3. Blur detection via Laplacian variance
    const blurVariance = computeLaplacianVariance(gray, cw, ch)
    if (blurVariance < THRESHOLDS.BLUR_VARIANCE_MIN) {
      warnings.push('blurry')
      hints.push('Hold steady and tap to focus on the label')
    }

    // AGC: 4. Edge density heuristic (proxy for "is there text/label visible?")
    const edgeDensity = computeEdgeDensity(gray, cw, ch)
    if (edgeDensity < THRESHOLDS.EDGE_DENSITY_MIN) {
      warnings.push('no_text_region')
      hints.push('Make sure the product label fills the frame')
    }

    // AGC: Pass if no critical warnings
    const pass = warnings.length === 0

    return { pass, warnings, hints }
  } catch (error) {
    // AGC: On any error, allow upload (don't block the user)
    console.warn('AGC: Image quality check failed, allowing upload:', error)
    return { pass: true, warnings: [], hints: [] }
  }
}

// =============================================================================
// AGC: Image processing helpers
// =============================================================================

/**
 * AGC: Convert RGBA pixel data to grayscale array
 * @param pixels - RGBA Uint8ClampedArray from canvas
 * @param w - Width
 * @param h - Height
 * @returns Grayscale values array (0-255)
 */
function toGrayscale(pixels: Uint8ClampedArray, w: number, h: number): Float32Array {
  const gray = new Float32Array(w * h)
  for (let i = 0; i < w * h; i++) {
    const offset = i * 4
    // AGC: Standard luminance formula (ITU-R BT.601)
    gray[i] = 0.299 * pixels[offset] + 0.587 * pixels[offset + 1] + 0.114 * pixels[offset + 2]
  }
  return gray
}

/**
 * AGC: Compute Laplacian variance as a blur metric.
 * AGC: Higher variance = sharper image. Low variance = blurry.
 * AGC: Uses a 3x3 Laplacian kernel: [0,1,0; 1,-4,1; 0,1,0]
 * @param gray - Grayscale pixel values
 * @param w - Width
 * @param h - Height
 * @returns Variance of the Laplacian response
 */
function computeLaplacianVariance(gray: Float32Array, w: number, h: number): number {
  let sum = 0
  let sumSq = 0
  let count = 0

  // AGC: Apply Laplacian kernel, skip border pixels
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x
      const lap =
        gray[idx - w] +             // AGC: top
        gray[idx - 1] +             // AGC: left
        -4 * gray[idx] +            // AGC: center
        gray[idx + 1] +             // AGC: right
        gray[idx + w]               // AGC: bottom

      sum += lap
      sumSq += lap * lap
      count++
    }
  }

  if (count === 0) return 0
  const mean = sum / count
  return sumSq / count - mean * mean
}

/**
 * AGC: Compute edge density as a proxy for text/label presence.
 * AGC: Uses a simplified Sobel-like gradient magnitude.
 * AGC: Returns the fraction of pixels above the edge threshold.
 * @param gray - Grayscale pixel values
 * @param w - Width
 * @param h - Height
 * @returns Edge density ratio (0..1)
 */
function computeEdgeDensity(gray: Float32Array, w: number, h: number): number {
  const EDGE_THRESHOLD = 30
  let edgePixels = 0
  let totalPixels = 0

  // AGC: Compute gradient magnitude using simple differences
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x
      // AGC: Horizontal gradient
      const gx = gray[idx + 1] - gray[idx - 1]
      // AGC: Vertical gradient
      const gy = gray[idx + w] - gray[idx - w]
      const magnitude = Math.sqrt(gx * gx + gy * gy)

      if (magnitude > EDGE_THRESHOLD) {
        edgePixels++
      }
      totalPixels++
    }
  }

  return totalPixels > 0 ? edgePixels / totalPixels : 0
}
